'use client';

import React, { useMemo } from 'react';
import useGetPayerMixReimbursement from 'src/hooks/payerMix/useGetPayerMixReimbursement';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { formatCurrency } from 'src/utils/formatCurrency/formatCurrency';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';

const PREVIEW_MAX_ITEMS = 2;

interface FlatPayer {
  code: string;
  payerName: string;
  payerType: string;
  avgReimbursement: number;
  stateAvgReimbursement: number;
}

const CompactReimbursementPreview: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();

  const { providerId, profileType } = getEntityIdentifiers(entityDetails);
  const filterSetId = widgetConfig?.filterSet?.id;

  const { data, isLoading } = useGetPayerMixReimbursement({
    providerId,
    location: profileType,
    filterSetId,
  });

  const flatPayers = useMemo<FlatPayer[]>(() => {
    if (!data?.results) return [];

    const rows: FlatPayer[] = [];
    for (const codeItem of data.results) {
      for (const payer of codeItem.payers ?? []) {
        rows.push({
          code: codeItem.code_name || codeItem.code,
          payerName: payer.payer_name,
          payerType: payer.payer_type,
          avgReimbursement: payer.avg_reimbursement_predicted ?? 0,
          stateAvgReimbursement: payer.state_avg_reimbursement_predicted ?? 0,
        });
      }
    }

    return rows
      .sort((a, b) => b.avgReimbursement - a.avgReimbursement)
      .slice(0, PREVIEW_MAX_ITEMS);
  }, [data]);

  if (isLoading || widgetConfig?.isLoading) {
    return <WidgetLoading data-testid="compact-reimbursement-preview-loading" aria-label="Loading reimbursement" />;
  }

  if (flatPayers.length === 0) {
    return <WidgetEmpty data-testid="compact-reimbursement-preview-empty" />;
  }

  return (
    <div
      className="flex flex-col"
      data-testid="compact-reimbursement-preview"
    >
      {/* Header */}
      <div className="flex items-center gap-2 pb-2 border-b border-neutral-200">
        <span className="text-xs font-bold text-neutral-600 flex-1 min-w-0">
          Payer
        </span>
        <span className="text-xs font-bold text-neutral-600 w-20 text-right">
          Avg. Reimb.
        </span>
        <span className="text-xs font-bold text-neutral-600 w-20 text-right">
          State Avg.
        </span>
      </div>

      {/* Rows */}
      {flatPayers.map((row, index) => (
        <div
          key={`${row.code}-${row.payerName}-${row.payerType}-${index}`}
          className="flex items-center gap-2 py-2 border-b border-neutral-100 last:border-b-0"
          data-testid={`reimbursement-preview-row-${index}`}
        >
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-sm font-medium text-primary truncate">
              {row.payerName}
            </span>
            <span className="text-xs text-neutral-500 truncate">
              {row.payerType} · {row.code}
            </span>
          </div>
          <span className="text-sm text-primary w-20 text-right tabular-nums">
            {formatCurrency(row.avgReimbursement)}
          </span>
          <span className="text-sm text-primary w-20 text-right tabular-nums">
            {formatCurrency(row.stateAvgReimbursement)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default CompactReimbursementPreview;
